import { ChangeEvent, FC, memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { selectAllTokens, selectInitialPrice, selectQuoteToken } from 'state/manage/selectors'
import NumericInput from 'components/Input/NumericInput'
import { SectionHeader, SectionTitle } from 'sections/market/mobile'
import { formatCurrency } from 'utils/prices'
import { setInitialPrice } from 'state/manage/reducer'
import { selectOraclePrices } from 'state/prices/selectors'
import { QUOTE_COINS } from '@bitly/sdk/constants'
import { selectNetwork } from 'state/app/selectors'

const MobileInitialPriceInput: FC = memo(() => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const initialPrice = useAppSelector(selectInitialPrice)
	const oraclePrices = useAppSelector(selectOraclePrices)
	const quoteToken = useAppSelector(selectQuoteToken)
	const allTokens = useAppSelector(selectAllTokens)
	const networkId = useAppSelector(selectNetwork)

	const tokenInfo = useMemo(() => {
		return allTokens.find(e => e.address === quoteToken)
	}, [quoteToken, allTokens])

	const price = useMemo(() => {
		if (quoteToken && oraclePrices && allTokens) {
			const targetToken = allTokens.find((token) => token.address === quoteToken)
			return (oraclePrices[targetToken?.symbol ?? ''] ?? 0) * Number(initialPrice)
		}
		return 0
	}, [quoteToken, oraclePrices, allTokens, initialPrice])

	const tradeAmount = useMemo(() => {
		if (quoteToken && QUOTE_COINS[networkId!.toString()].includes(quoteToken)) {
			return Number(initialPrice) * (price || 0)
		}
		return Number(initialPrice)
	}, [initialPrice, price, quoteToken, networkId])

	const showAmountInUSD = useMemo(() => {
		return quoteToken ? QUOTE_COINS[networkId!.toString()].includes(quoteToken) : false
	}, [quoteToken, networkId])

	const onChangeValue = (_: ChangeEvent<HTMLInputElement>, v: string) => {
		dispatch(setInitialPrice(v))
	}

	return (
		<div>
			<SectionHeader>
				<SectionTitle>{t('manage.list-pair.modal.initial-price')}</SectionTitle>
			</SectionHeader>
			<MainInput>
				<InputWrapper>
					<SwapTextInput
						value={initialPrice}
						onChange={(_, value) => {
							onChangeValue(_, value)
						}}
						placeholder="0.00"
						roundedCorner={false}
						noShadow
					/>
					<SwapCurrencyPrice data-testid="amount-value">
						{showAmountInUSD && tokenInfo?.symbol && tradeAmount != null
							? formatCurrency(tokenInfo.symbol, tradeAmount, { sign: '$' })
							: null}
					</SwapCurrencyPrice>
				</InputWrapper>
				<TokenDisplay>
					{tokenInfo?.symbol ?? 'Select'}
				</TokenDisplay>
			</MainInput>
		</div>
	)
})

const MainInput = styled.div`
	box-sizing: border-box;
	border: ${(props) => props.theme.colors.selectedTheme.border};
	border-radius: 10px;
	padding: 10px;
	padding-left: 0;
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 15px;
`

const SwapTextInput = styled(NumericInput)`
	background-color: transparent;
	border: none;
	color: ${(props) => props.theme.colors.selectedTheme.text.value};
	height: initial;

	& > input {
		font-size: 19px;
		line-height: 1;
		margin-bottom: 4px;
	}
`

const SwapCurrencyPrice = styled.div`
	font-size: 12px;
	color: ${(props) => props.theme.colors.selectedTheme.gray};
	height: 12px;
	margin-left: 10px;
`

const InputWrapper = styled.div`
	position: relative;
	flex: 1;
`

const TokenDisplay = styled.div`
	background: ${(props) => props.theme.colors.selectedTheme.button.fill};
	padding: 6px;
	padding-left: 5px;
	margin-left: 10px;
	border-radius: 8px;
	border: ${(props) => props.theme.colors.selectedTheme.border};
	color: ${(props) => props.theme.colors.selectedTheme.text.value};
	display: flex;
	align-items: center;
	font-family: ${(props) => props.theme.fonts.regular};
	font-size: 15px;
`

export default MobileInitialPriceInput
